<script lang="ts">
  import EmptyState from '../../components/ui/EmptyState.svelte';
  import Button from '../../components/ui/Button.svelte';
  import Spinner from '../../components/ui/Spinner.svelte';
  import {
    fetchDonations,
    donationTypeLabel,
    formatAmount,
    type DonationsPayload,
  } from '../../lib/donations';
  import { AsyncData } from '../../lib/async-data.svelte';

  // Account → Donations tab. Loads payload from /api/account/donations
  // on mount: donation history (newest first), active subscription card
  // if currently billing, and a Stripe billing-portal session URL the
  // user opens in a new tab to manage their card or cancel.

  const view = new AsyncData<DonationsPayload>(fetchDonations);

  const formatDate = (iso: string | null): string =>
    iso ? new Date(iso).toLocaleDateString() : '—';
</script>

{#if view.loading}
  <div class="donations-loading"><Spinner size="lg" /></div>
{:else if view.data && (view.data.donations.length > 0 || view.data.active_subscription)}
  <div class="donations">
    {#if view.data.active_subscription}
      <section class="donations__active">
        <div>
          <h3 class="donations__section-title">Active subscription</h3>
          <p class="donations__active-status">
            Status: <strong>{view.data.active_subscription.status}</strong>
          </p>
        </div>
        {#if view.data.portal_url}
          <Button variant="secondary" size="sm" href={view.data.portal_url}>
            Manage subscription
          </Button>
        {/if}
      </section>
    {/if}

    {#if view.data.donations.length > 0}
      <section class="donations__history">
        <h3 class="donations__section-title">History</h3>
        <div class="donations__table-wrap">
          <table class="donations__table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {#each view.data.donations as row (row.id)}
                <tr class:donations__row--refunded={row.status === 'refunded'}>
                  <td>{formatDate(row.paid_at)}</td>
                  <td>{donationTypeLabel(row.type)}</td>
                  <td class="donations__amount">
                    {formatAmount(row.amount_cents, row.currency)}
                  </td>
                  <td class="donations__status donations__status--{row.status}">
                    {row.status}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>
    {/if}

    {#if view.data.portal_url && !view.data.active_subscription}
      <p class="donations__portal-note">
        Need a receipt or to update your card?
        <a href={view.data.portal_url} target="_blank" rel="noopener noreferrer">
          Open the billing portal &rarr;
        </a>
      </p>
    {/if}
  </div>
{:else}
  <EmptyState
    title="No donations yet"
    body="Help keep AtlasGuess ad-free and earn a donor badge that shows on the leaderboard and your profile."
  >
    {#snippet action()}
      <Button variant="primary" href="/donate">Donate</Button>
    {/snippet}
  </EmptyState>
{/if}
